import { createSelector, Selector } from '@reduxjs/toolkit'
import { productSectorStoreSelector, productStoreSelector } from './storeSelectors'
import {
  ProductSector,
  Product,
  EntityType,
  CollateralBasketType,
  Market,
  ProductDetail,
  ProductSectorDetail,
  Permission,
  ProductSectorMappingStateEvent,
  SectorStatus
} from 'api'
import { GlobalState } from 'store'
import {
  createAllEntitiesSelector,
  createAllEntitiesSortedSelector,
  createAllEntityUpdatesSelector,
  createEntityGetterSelector,
  createEntityStoreSelector
} from './entitySelectors'
import { compareDateDesc, compareInsensitive, notNilFilter, ParametricSelector } from 'utils'
import { ProductSectorHelper } from 'model'
import { isNotNil } from 'utils/predicate'
import { uniq } from 'lodash'
import { createUserHasPermissionSelector } from './userSelectors'
import { ProductSectorMappingObject, ProductSectorMappingDraftObject } from 'api/local'

export const productDetailsSelector: Selector<GlobalState, ProductDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.PRODUCT),
  productUpdates => {
    return productUpdates.map(update => {
      const product = update.entity
      const availableEvents = update.availableEvents
      return { product, availableEvents }
    })
  }
)

export const productIdsWithProductSector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSelector(EntityType.PRODUCT_SECTOR),
  productSectors => {
    return uniq(productSectors.map(productSector => productSector.productId))
  }
)

export const productSectorDetailsSelector: Selector<GlobalState, ProductSectorDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.PRODUCT),
  createEntityGetterSelector(EntityType.REFERENCE_RATE),
  (productSectorUpdates, getProduct, getReferenceRate) => {
    return productSectorUpdates.map(update => {
      const productSector = update.entity
      const product = getProduct(productSector.productId)
      const referenceRates = Object.keys(productSector.defaultFloatCrystallisationDaysByReferenceRateId)
        .map(referenceRateId => getReferenceRate(referenceRateId))
        .filter(notNilFilter)
      const availableEvents = update.availableEvents
      return { productSector, product, referenceRates, availableEvents }
    })
  }
)

export const canViewSectorMappingSelector = createUserHasPermissionSelector(Permission.SECTOR_MAPPING_VIEW)
export const canEditSectorMappingSelector = createUserHasPermissionSelector(Permission.SECTOR_MAPPING_MANAGE)
export const canViewOrEditSectorMappingSelector = createSelector(
  canViewSectorMappingSelector,
  canEditSectorMappingSelector,
  (hasViewPermission, hasEditPermission) => {
    return hasViewPermission || hasEditPermission
  }
)

export const productSectorMappingDetailSelector: Selector<GlobalState, ProductSectorMappingObject[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.PRODUCT_SECTOR_MAPPING),
  canViewOrEditSectorMappingSelector,
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.PRODUCT),
  (productSectorUpdates, hasViewOrEditPermission, getProductSector, getProduct) => {
    if (!hasViewOrEditPermission) {
      return []
    }
    return productSectorUpdates
      .map(update => {
        const productSector = getProductSector(update.entity.productSectorMapping.productSectorId)
        const product = getProduct(productSector?.productId)
        return {
          ...update.entity,
          productName: product?.name,
          productSectorName: productSector?.name
        }
      })
      .sort((v1, v2) => compareDateDesc(v1.lastUpdatedAt, v2.lastUpdatedAt))
  }
)

export const productSectorMappingDraftDetailSelector: Selector<GlobalState, ProductSectorMappingDraftObject[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.PRODUCT_SECTOR_MAPPING_DRAFT),
  canEditSectorMappingSelector,
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.PRODUCT),
  (productSectorUpdates, hasManagePermission, getProductSector, getProduct) => {
    if (!hasManagePermission) {
      return []
    }
    return productSectorUpdates
      .map(update => {
        const productSector = getProductSector(update.entity.productSectorMapping.productSectorId)
        const product = getProduct(productSector?.productId)
        return {
          ...update.entity,
          productName: product?.name,
          productSectorName: productSector?.name
        }
      })
      .sort((v1, v2) => compareDateDesc(v1.lastUpdatedAt, v2.lastUpdatedAt))
  }
)

export const productSectorMappingHasChangesSelector: Selector<GlobalState, boolean> = createSelector(
  [createEntityStoreSelector(EntityType.PRODUCT_SECTOR_MAPPING_STATE)],
  productSectorUpdates => {
    return productSectorUpdates.default.lastUpdate.availableEvents.some(event =>
      [ProductSectorMappingStateEvent.ROLLBACK_CHANGES, ProductSectorMappingStateEvent.SUBMIT_CHANGES].includes(event)
    )
  }
)

export const knownProductSectorStoreSelector = createSelector(productSectorStoreSelector, productSectorStore =>
  Object.fromEntries(Object.entries(productSectorStore).filter(([_, value]) => !value.lastUpdate.entity.system))
)

export const productSectorRecordSelector = createSelector(knownProductSectorStoreSelector, productSectorStore =>
  Object.keys(productSectorStore).reduce((accumulator, current) => {
    const productSector = productSectorStore[current].lastUpdate.entity
    if (productSector.status !== SectorStatus.DELETED) {
      accumulator[current] = productSectorStore[current].lastUpdate.entity
    }
    return accumulator
  }, {} as Record<string, ProductSector>)
)

export const clearableProductSectorRecordSelector = createSelector(knownProductSectorStoreSelector, productSectorStore =>
  Object.keys(productSectorStore).reduce((accumulator, current) => {
    const productSector = productSectorStore[current].lastUpdate.entity
    if (productSector.basketType !== CollateralBasketType.EUROCLEAR && productSector.status !== SectorStatus.DELETED) {
      accumulator[current] = productSectorStore[current].lastUpdate.entity
    }
    return accumulator
  }, {} as Record<string, ProductSector>)
)

export const productRecordSelector = createSelector(productStoreSelector, productStore =>
  Object.keys(productStore).reduce((previous, current) => {
    previous[current] = productStore[current].lastUpdate.entity
    return previous
  }, {} as Record<string, Product>)
)

export const createProductGetterSelector: Selector<GlobalState, (productSectorId: string) => Product | null> = createSelector(
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.PRODUCT),
  (getProductSector, getProduct) => (productSectorId: string) => {
    const productSector = getProductSector(productSectorId)
    return productSector && getProduct(productSector.productId)
  }
)

export const createSortedProductSectorIdsSelector = <TProps>(
  marketSelector: ParametricSelector<{}, TProps, Market | null | undefined>,
  bothMarkets: ParametricSelector<{}, TProps, Boolean | null | undefined>
): ParametricSelector<GlobalState, TProps, string[]> =>
  createSelector(
    [createAllEntitiesSelector(EntityType.PRODUCT_SECTOR), createEntityGetterSelector(EntityType.PRODUCT), marketSelector, bothMarkets],
    (productSectors, getProduct, market, bothMarkets) =>
      productSectors
        .filter(productSector => {
          const product = getProduct(productSector.productId)
          if (bothMarkets) {
            return !productSector.basketType
          }
          return market && market === product?.market && !productSector.basketType
        })
        .filter(productSector => productSector.status !== SectorStatus.DELETED)
        .sort((ps1, ps2) => compareInsensitive(ProductSectorHelper.getName(ps1), ProductSectorHelper.getName(ps2)))
        .map(productSector => productSector.id)
  )

export const productSectorByNameGetterSelector = createSelector(
  createAllEntitiesSelector(EntityType.PRODUCT_SECTOR),
  allProductSectors => (name: string) => allProductSectors.find(productSector => productSector.name === name) ?? null
)

export const nonGcBespokeProductSectorIdsSelector = createSelector(createAllEntitiesSelector(EntityType.PRODUCT_SECTOR), productSectors =>
  productSectors
    .filter(productSector => productSector.basketType !== CollateralBasketType.GC_BESPOKE)
    .map(productSector => productSector.id)
)

export const productSectorsByNameAndProductNameSelector: Selector<
  GlobalState,
  (productName: string, name: string | null) => ProductSector[]
> = createSelector(
  [createAllEntitiesSelector(EntityType.PRODUCT_SECTOR), createEntityGetterSelector(EntityType.PRODUCT)],
  (allProductSectors, getProduct) => (productName, name) =>
    allProductSectors.filter(productSector => {
      const product = getProduct(productSector.productId)
      return product?.name === productName && (!name || productSector.name === name)
    })
)

export const isProductSectorBasketTypeGCGetterSelector = createSelector(
  [createEntityGetterSelector(EntityType.PRODUCT_SECTOR)],
  getProductSector => (productSectorId: string) => {
    const productSector = getProductSector(productSectorId)
    return isNotNil(productSector && productSector.basketType === CollateralBasketType.GC_BESPOKE)
  }
)

export const allKnownProductSectorsGetterSelector = createSelector(
  createAllEntitiesSelector(EntityType.PRODUCT_SECTOR),
  allProductSectors => allProductSectors.filter(productSector => !productSector.system)
)

export const allKnownSortedProductSectorIdsSelector = createSelector(
  createAllEntitiesSortedSelector(EntityType.PRODUCT_SECTOR),
  productSectors =>
    productSectors
      .filter(productSector => !productSector.system)
      .filter(productSector => productSector.status !== SectorStatus.DELETED)
      .map(productSector => productSector.id)
)

export const getProductSectorFromProductSectorId: Selector<
  GlobalState,
  (productSectorId: string | null) => ProductSector | null
> = createSelector(createEntityGetterSelector(EntityType.PRODUCT_SECTOR), getProductSector => (productSectorId: string | null) =>
  productSectorId ? getProductSector(productSectorId) : null
)

export const productByProductSectorIdGetterSelector: Selector<
  GlobalState,
  (productSectorId: string | null | undefined) => Product | null
> = createSelector(
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  createEntityGetterSelector(EntityType.PRODUCT),
  (getProductSector, getProduct) => productSectorId => {
    const productSector = getProductSector(productSectorId)
    const product = getProduct(productSector?.productId)

    return product
  }
)
